import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
import { getWhatsAppUrl } from '../utils/whatsapp';
type Message = {
  id: number;
  from: 'bot' | 'user';
  text: React.ReactNode;
};
type Option = {
  label: string;
  key: string;
};
const MAIN_MENU: Option[] = [{
  label: '💰 Ver precios',
  key: 'precios'
}, {
  label: '😌 ¿Duele el tratamiento?',
  key: 'dolor'
}, {
  label: '🔁 ¿Cuántas sesiones necesito?',
  key: 'sesiones'
}, {
  label: '🌗 ¿Sirve para mi tipo de piel?',
  key: 'piel'
}, {
  label: '📍 Ubicación y horarios',
  key: 'ubicacion'
}, {
  label: '📅 Quiero agendar mi consulta',
  key: 'agendar'
}];
const PRICES: {
  label: string;
  price: number;
}[] = [{
  label: 'Cuerpo completo (mujer)',
  price: 2000
}, {
  label: 'Cuerpo completo (hombre)',
  price: 2500
}, {
  label: 'Pecho y abdomen',
  price: 1200
}, {
  label: 'Espalda',
  price: 1200
}, {
  label: 'Piernas',
  price: 1200
}, {
  label: 'Axilas',
  price: 800
}, {
  label: 'Zona íntima',
  price: 650
}, {
  label: 'Tira de cola',
  price: 650
}, {
  label: 'Barba (hombre)',
  price: 800
}, {
  label: 'Bozo y mentón (mujer)',
  price: 650
}];
const BOT_REPLIES: Record<string, React.ReactNode> = {
  dolor: 'Con la tecnología Láser Soprano el tratamiento es prácticamente indoloro 😌. Su sistema de enfriamiento mantiene la piel a una temperatura cómoda durante toda la sesión, por eso es apto incluso para pieles sensibles.',
  sesiones: 'En promedio se necesitan entre 8 y 12 sesiones para una reducción efectiva del vello. La cantidad exacta depende de la zona, el tipo de piel y de vello. En la evaluación gratuita te damos un plan personalizado. 🔁',
  piel: 'Sí 🌗. El Láser Soprano es seguro para todo tipo de piel, incluso pieles morenas o bronceadas, gracias a su tecnología de barrido en movimiento. ¡Lo evaluamos sin costo en tu primera consulta!',
  ubicacion: <span data-id="element-28">
      Estamos en <strong data-id="element-29">Avda. Uruguay 1788, Cordón, Montevideo</strong> 📍
      <br data-id="element-30" />
      Horarios: Lunes a Viernes 13:30–23:00 y Sábados 7:00–16:00.
    </span>
};
export const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showMenu, setShowMenu] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showWhatsAppCta, setShowWhatsAppCta] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(0);
  const nextId = () => {
    idRef.current += 1;
    return idRef.current;
  };
  // Auto-open with welcome shortly after entering the page
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (isOpen && !hasGreeted) {
      setHasGreeted(true);
      setMessages([{
        id: nextId(),
        from: 'bot',
        text: '¡Hola! 👋 Soy Sofía, de Depilación Láser Uruguay. Estoy para ayudarte a resolver tus dudas. ¿Qué te gustaría saber?'
      }]);
    }
  }, [isOpen, hasGreeted]);
  useEffect(() => {
    endRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, showWhatsAppCta]);
  const pushBot = (text: React.ReactNode) => setMessages(prev => [...prev, {
    id: nextId(),
    from: 'bot',
    text
  }]);
  const pushUser = (text: React.ReactNode) => setMessages(prev => [...prev, {
    id: nextId(),
    from: 'user',
    text
  }]);
  const handleOption = (option: Option) => {
    pushUser(option.label.replace(/^[^\s]+\s/, ''));
    setShowMenu(false);
    setShowWhatsAppCta(false);
    setTimeout(() => {
      if (option.key === 'precios') {
        pushBot(<div data-id="element-31">
            <p className="mb-2" data-id="element-32">Estos son nuestros precios por sesión 💰:</p>
            <ul className="space-y-1" data-id="element-33">
              {PRICES.map(p => <li key={p.label} className="flex justify-between gap-3 border-b border-slate-100 pb-1" data-id="element-34">
                  <span data-id="element-35">{p.label}</span>
                  <span className="font-bold text-brand-600" data-id="element-36">${p.price}</span>
                </li>)}
            </ul>
            <p className="mt-2 text-slate-500" data-id="element-37">
              Consultá por packs promocionales en tu evaluación gratuita.
            </p>
          </div>);
      } else if (option.key === 'agendar') {
        pushBot('¡Genial! 📅 La evaluación es gratuita y sin compromiso. Te derivo a WhatsApp para coordinar tu horario con el equipo.');
        setShowWhatsAppCta(true);
        setTimeout(() => setShowMenu(true), 400);
        return;
      } else {
        pushBot(BOT_REPLIES[option.key]);
      }
      setTimeout(() => {
        pushBot('¿Querés consultar algo más?');
        setShowWhatsAppCta(true);
        setShowMenu(true);
      }, 600);
    }, 500);
  };
  const whatsappMessage = 'Hola! Estuve consultando en la web con el asistente virtual y me gustaría agendar mi evaluación gratuita.';
  return <>
      {/* Launcher */}
      <AnimatePresence data-id="element-38">
        {!isOpen && <motion.button initial={{
        scale: 0,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 18,
        stiffness: 260
      }} whileHover={{
        scale: 1.04
      }} whileTap={{
        scale: 0.95
      }} onClick={() => setIsOpen(true)} className="fixed bottom-5 right-4 sm:bottom-6 sm:right-6 z-50 group inline-flex w-auto items-center gap-2 sm:gap-3 bg-brand-600 text-white pl-1.5 pr-3 sm:pr-5 py-1.5 sm:py-2 rounded-full shadow-xl shadow-brand-900/30 hover:bg-brand-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500" aria-label="Abrir asistente virtual" data-id="element-39">
            {/* Pulsing glow rings */}
            <span className="absolute inset-0 rounded-full bg-brand-500 animate-ping opacity-20" data-id="element-40"></span>
            <motion.span aria-hidden animate={{
          boxShadow: ['0 0 0 0 rgba(58,107,165,0)', '0 0 26px 4px rgba(58,107,165,0.6)', '0 0 0 0 rgba(58,107,165,0)']
        }} transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full" data-id="element-41" />

            {/* Avatar */}
            <span className="relative z-10 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white overflow-hidden flex items-center justify-center shrink-0 ring-2 ring-white/40" data-id="element-42">
              <img src="https://cdn.magicpatterns.com/uploads/7kvJW8m3bLhnmJS1ZmPMSn/WhatsApp_Image_2026-06-09_at_09.43.25.jpg" alt="" className="w-full h-full object-cover" data-id="element-43" />
            </span>

            {/* Label */}
            <span className="relative z-10 text-left leading-tight pr-1 shrink-0" data-id="element-44">
              <span className="block text-[13px] sm:text-sm font-bold whitespace-nowrap" data-id="element-45">
                Chateá con Sofía
              </span>
              <span className="hidden sm:flex items-center gap-1 text-[11px] text-brand-100 font-medium whitespace-nowrap" data-id="element-46">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" data-id="element-47"></span>
                En línea ahora
              </span>
            </span>
          </motion.button>}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence data-id="element-48">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} transition={{
        type: 'spring',
        damping: 24,
        stiffness: 280
      }} className="fixed bottom-4 right-4 left-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 sm:w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" style={{
        height: 'min(600px, calc(100vh - 2rem))'
      }} role="dialog" aria-label="Asistente virtual" data-id="element-49">
            {/* Header */}
            <div className="bg-navy-900 px-5 py-4 flex items-center justify-between shrink-0" data-id="element-50">
              <div className="flex items-center gap-3" data-id="element-51">
                <div className="w-10 h-10 rounded-full bg-white overflow-hidden flex items-center justify-center shrink-0" data-id="element-52">
                  <img src="https://cdn.magicpatterns.com/uploads/7kvJW8m3bLhnmJS1ZmPMSn/WhatsApp_Image_2026-06-09_at_09.43.25.jpg" alt="Depilación Láser Uruguay" className="w-full h-full object-cover" data-id="element-53" />
                </div>
                <div data-id="element-54">
                  <p className="text-white font-bold text-sm leading-tight" data-id="element-55">
                    Sofía
                  </p>
                  <p className="text-brand-300 text-xs flex items-center gap-1" data-id="element-56">
                    <span className="w-2 h-2 bg-green-400 rounded-full inline-block" data-id="element-57"></span>
                    En línea
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition-colors p-1" aria-label="Cerrar asistente" data-id="element-58">
                <X className="w-5 h-5" data-id="element-59" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50" data-id="element-60">
              {messages.map(msg => <div key={msg.id} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-61">
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${msg.from === 'user' ? 'bg-brand-600 text-white rounded-br-md' : 'bg-white text-slate-700 border border-slate-100 rounded-bl-md shadow-sm'}`} data-id="element-62">
                    {msg.text}
                  </div>
                </div>)}

              {showWhatsAppCta && <div className="flex justify-start" data-id="element-63">
                  <a href={getWhatsAppUrl(whatsappMessage)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold text-sm px-4 py-2.5 rounded-full hover:bg-[#20bd5a] transition-colors shadow-sm" data-id="element-64">
                    <Send className="w-4 h-4" data-id="element-65" />
                    Continuar por WhatsApp
                  </a>
                </div>}

              <div ref={endRef} data-id="element-66" />
            </div>

            {/* Quick replies */}
            {showMenu && <div className="border-t border-slate-100 bg-white px-3 py-3 shrink-0 max-h-[40%] overflow-y-auto" data-id="element-67">
                <div className="flex flex-wrap gap-2" data-id="element-68">
                  {MAIN_MENU.map(option => <button key={option.key} onClick={() => handleOption(option)} className="text-xs font-medium text-slate-700 bg-slate-100 hover:bg-brand-50 hover:text-brand-700 border border-slate-200 px-3 py-2 rounded-full transition-colors" data-id="element-69">
                      {option.label}
                    </button>)}
                </div>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </>;
};